import { Link } from "react-router-dom";

const Manual = () => {
    const name = process.env.REACT_APP_APP_NAME
    return (
        <>
            <div className="policyMainContainer">
                {/* Header */}
                <header className="policyHeaderContainer">
                    <h1 className="policyTitle">The {name} Manual: Step-by-Step Guide</h1>
                    <p className="policyDate">
                        Your Potential, Our Platform—No Matter Where You Live, ...
                    </p>
                </header>

                <section className="policySection">
                    <p className="policyIntro">
                        Before you begin your journey toward success, follow these simple steps to prepare your digital classroom.
                    </p>
                </section>

                <section className="policySection">
                    <h2>Step 1: Secure Your Access (Account Verification)</h2>
                    <p>
                        Before you can begin any assessment, you must have a verified and active {name} Account..
                    </p>

                    <h3>A. Getting Started</h3>
                    <ul className="policyList">
                        <li>
                            <strong>New Users:</strong> If you do not have an account yet, click on <Link to="/signup">"Sign Up" </Link>
                            or <Link to="/signup">"Register"</Link> to create one.
                            Once your account is created, return to the Test Page.
                        </li>
                        <li>
                            <strong>Existing Users:</strong> If you already have an account, simply click <Link to="/login">"Login"</Link>
                            and enter your credentials.
                        </li>
                    </ul>

                    <h3>B. How to Perform a "Login Check"</h3>
                    <p>
                        To ensure you are ready to start, follow these steps:
                    </p>
                    <ol className="policyList">
                        <li>
                            <strong>Locate Your Profile:</strong> Look for the <Link to="/profile">Profile</Link> Link or User
                            <Link to="/profile">Icon</Link> on your screen
                            (usually in the top-right corner).
                        </li>
                        <li>
                            <strong>Go to Dashboard:</strong> Click the link to visit your Student <Link to="/profile">Dashboard</Link>.
                        </li>
                        <li>
                            <strong>Verify Your Details:</strong> * Check if your <strong>Name</strong> and <strong>Email ID</strong> are
                            displayed correctly.
                            <ul className="policyList">
                                <li>
                                    <strong>Account Status:</strong> Ensure your status is marked as <strong>Active</strong>.
                                </li>
                                <li>
                                    If you see these details, you are successfully logged in and authorized to take the test.
                                </li>
                            </ul>
                        </li>
                    </ol>

                    <h3>C. Why This Is Essential</h3>

                    <ul className="policyList">
                        <li>
                            Data Security: Your test responses are linked directly to your unique ID. Being logged in ensures
                            that every answer you provide is safely stored in our database.
                        </li>
                        <li>
                            Performance Tracking: Once you finish, our system evaluates your performance. Your Scorecard, detailed
                            solutions, and progress reports are stored permanently in your account.
                        </li>
                        <li>
                            Future Reference: Being logged in allows you to revisit your Test History, review your previous
                            responses, and track your academic growth at any time in the future.
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>Step 2: Building Your Personal Test Library</h2>
                    <p>
                        At {name}, we host a vast range of examinations—from <strong>SSC, Banking, and Railways</strong> to
                        <strong>IIT-JEE, NEET and various state boards</strong>. Because every student's path is unique, your
                        tests do not appear automatically. You must "claim" the subjects that align with your career goals
                        to build your own library.
                    </p>
                    <h3>A. Navigate to the "Course Page"</h3>
                    <p>
                        Start by browsing our extensive library. We have categorized tests by exam type and difficulty level to make
                        your search easier.
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Explore Categories:</strong> Find your specific exam (e.g., Central Government Jobs, Entrance Exams,
                            or Academic Boards).
                        </li>
                        <li>
                            <strong>Create a Wishlist:</strong> If you find a course you are interested in but aren't ready to start yet,
                            you can save it to your wishlist for later.
                        </li>
                    </ul>
                    <h3>B. "Add" Tests to Your bucket</h3>
                    <p>
                        Once you identify the subjects you need for your preparation:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Action:</strong> Click the <strong>"Add to My Tests"</strong> or <strong>"Enroll"</strong> button.
                        </li>
                        <li>
                            <strong>Result:</strong> This test will now be permanently linked to your Student Dashboard, making it available
                            every time you log in.
                        </li>
                    </ul>

                    <h3>C. The Power of Customization</h3>
                    <p>
                        This step is designed to protect your mental focus.
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Filter the Noise:</strong> By selecting only what you need, you remove unnecessary distractions and
                            "information overload."
                        </li>
                        <li>
                            <strong>Goal Alignment:</strong> Your dashboard will now only show exams that matter to your career, helping you
                            stay organized and stress-free.
                        </li>
                    </ul>
                    <p>
                        After completing this task you are now ready for testing, go to the <Link to="/test">Test </Link>
                        Page and start a new test.
                    </p>
                </section>

                <section className="policySection">
                    <h2>Step 3: Selecting Your Subject & Test Configuration</h2>
                    <p>
                        Now that your account is active and your library is built, it is time to configure your specific test session.
                        On the <Link to="/test">Test</Link> Page, you will find a series of mandatory selection menus. Each step must be
                        completed to unlock the "Start" button.
                    </p>
                    <h3>3.1. Select Your Exam</h3>
                    <p>
                        Choose the primary exam category you are targeting (e.g., SSC, Banking, or Railway). Only the exams you added to
                        your library in Step 2 will appear here.
                    </p>

                    <h3>3.2. Select Your Paper</h3>
                    <p>
                        Select the specific paper or tier associated with your chosen exam (e.g., Tier-I, Tier-II, or Prelims/Mains).
                    </p>

                    <h3>3.3. Select Test "Type"</h3>
                    <p>
                        This is where you define your practice style. Based on your selection, different sub-options will appear:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>3.3.1. Previous Year Papers:</strong> If selected, a <strong>Year List</strong> will appear.
                            Choose the specific year (e.g., 2022, 2023) to see the exact questions asked by the conducting body
                            in that period.
                        </li>
                        <li>
                            <strong>3.3.2. Test Series:</strong> If selected, you will see a <strong>Series Code </strong>
                            (indicating the Series set, subject, like Math or Science) and a <strong>Series Number</strong>
                            (identifying the specific set). This is perfect for structured, high-level preparation.
                        </li>
                        <li>
                            <strong>3.3.3. Practice Sets:</strong> A quick-fire option for general practice. No further sub-options
                            are required; simply select and proceed.
                        </li>
                        <li>
                            <strong>3.3.4. Subject-Wise:</strong> Choose this to focus on a single subject. A dropdown will appear
                            for you to select the specific subject (e.g., History, Physics, etc.).
                        </li>
                        <li>
                            <strong>3.3.5. Topic-Wise:</strong> For micro-level study,
                            <strong>select Subject &gt; Subject Name &gt; Specific Topic</strong> (e.g., Mathematics &gt; Algebra).
                            This allows you to master one chapter at a time.
                        </li>
                    </ul>

                    <h3>3.4. Language Selection</h3>
                    <p>
                        {name} is inclusive. Choose the language you are most comfortable with:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Bilingual Exams: </strong> If the exam is available in multiple languages (e.g., Hindi and English),
                            both options will appear.
                        </li>
                        <li>
                            <strong>Single Language: </strong> If the official exam is only conducted in one language, only that option
                            will be displayed.
                        </li>
                    </ul>

                    <h3>3.5. Timer Mode (Customized Learning)</h3>
                    <p>
                        You have the power to control your testing environment:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Timer ON (Exam Mode):</strong> We highly recommend this. You will be given the exact time allowed
                            in the real exam.
                            The test will <strong>automatically submit</strong> as soon as the clock reaches zero. This helps build
                            speed and manages
                            "Exam Hall Stress."
                        </li>
                        <li>
                            <strong>Timer OFF (Practice Mode):</strong> Ideal for deep learning. You can take your time to solve
                            difficult problems
                            without the pressure of the clock.
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>Step 4: Confirming Your Configuration</h2>
                    <p>
                        Once you have filled in all the mandatory fields (Exam, Paper, Type, Language, and Timer),
                        you are at the final stage of preparation.
                        <br />
                        Click the <strong>"Submit"</strong> button to start your test.
                    </p>
                    <h3>The "Submit" Action</h3>
                    <ul className="policyList">
                        <li>
                            <strong>The Final Check:</strong> Look at the summary of your choices. If everything is correct,
                            click the Submit button.
                        </li>
                        <li>
                            <strong>What happens next: </strong>The system will now "build" your test based on your specific requirements
                            (e.g., fetching the 2023 Previous Year Paper in Hindi with the Timer ON).
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>5. Final Step: Launching Your Test</h2>
                    <h3>5.1. The Confirmation Window</h3>
                    <p>
                        After you click the <strong>"Submit"</strong> button on the configuration page, a new screen will appear
                        featuring a <strong>pop-up confirmation window</strong>. This window serves as your final checkpoint. It is
                        essential to read all the information displayed—such as the number of questions, total time, and specific
                        instructions—very carefully to ensure you are fully prepared.
                    </p>
                    <h3>5.2. Choosing Your Path</h3>
                    <p>
                        On this pop-up screen, you will have two distinct options to choose from:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Start Option:</strong> If you are ready to begin, select "Start." Your test will launch immediately,
                            the timer will begin (if enabled), and your session will be active. This is the official beginning of your
                            success journey for this session.
                        </li>
                        <li>
                            <strong>Back Option:</strong> If you realize you have made a mistake in your selection or if you are not
                            quite ready, choose the "Back" option. Your current test session will be terminated, and you will be safely
                            returned to the main Test Page to adjust your settings or choose a different subject.
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>6. Navigating the Exam Screen</h2>
                    <p>
                        Once the test begins, your screen will transform into a professional examination environment. The layout is
                        designed to provide all necessary information at a glance.
                    </p>

                    <h3>6.1. Exam Header Information</h3>
                    <p>
                        The top of the screen displays essential details to keep you on track:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Exam & Paper Name: </strong> Confirms the specific test you are currently taking.
                        </li>
                        <li>
                            <strong>Question Number:</strong> Displayed in the top-middle of the screen so you always know your
                            current position.
                        </li>
                        <li>
                            <strong>Countdown Timer: </strong>Located in the <strong>top-right corner</strong>. If your timer is "On,"
                            it shows the remaining time. Once the time reaches zero, your exam will <strong>submit automatically</strong>.
                        </li>
                        <li>
                            <strong>Language Toggle:</strong> If the exam is <strong>Bilingual</strong>, a language change button will
                            be visible. Clicking this will switch the current question and its options to your preferred language.
                            <br />
                            Note: If the exam is only in one language, this button will stay hidden.
                        </li>
                    </ul>

                    <h3>6.2. Responsive Screen Layout</h3>
                    <p>
                        The display adapts to your device to ensure a smooth testing experience:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Desktop View:</strong> The question and options appear on the left, while the
                            <strong>Question Index</strong> (navigation list) is always visible on the right.
                        </li>
                        <li>
                            <strong>Mobile View:</strong> To save space, the Question Index is hidden by default. Click the Index
                            Button to show the list, and click it again to hide it and return to the question.
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>7. Understanding the Question Palette</h2>
                    <p>
                        The Question Index uses a color-coding system to help you track your progress instantly.
                        <br />
                        According to your system, the background colors of the question numbers mean:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Blue: </strong> Current Question (The one you are currently solving).
                        </li>
                        <li>
                            <strong>Black:</strong> Unseen Question (You have not reached this question yet).
                        </li>
                        <li>
                            <strong>Yellow: </strong> Unanswered Question (You have viewed the question but skipped it).
                        </li>
                        <li>
                            <strong>Green:</strong> Answered Question (Your response is saved).
                        </li>
                        <li>
                            <strong>Dark Red: </strong> Marked for Review (You have marked this to check again later).
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>8. Navigating Your Responses</h2>
                    <p>
                        The buttons on your exam screen are designed to help you manage your time and ensure your answers are
                        recorded correctly. Each action you take will update the background color of the question number in
                        your index list.
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Pre (Previous):</strong> Clicking this button allows you to move back and view the question
                            immediately preceding your current one. It does not change your saved answers or the index color.
                        </li>
                        <li>
                            <strong>Save & Next:</strong> This is your primary action button. It performs two different tasks based
                            on your selection:
                            <ul className="policyList">
                                <li>
                                    <strong>If no option is selected: </strong>You will move to the next question, and the index
                                    color will turn <strong>Yellow</strong>, indicating you have seen the question but left
                                    it unanswered.
                                </li>
                                <li>
                                    <strong>If an option is selected:</strong> Your response is securely saved to your account,
                                    you move to the next question, and the index color turns <strong>Green</strong>.
                                </li>
                            </ul>
                        </li>

                        <li>
                            <strong>Clear Response:</strong> Clicking this button clears the current selection on your browser.
                            The option becomes empty on your screen, but it does not affect any previously saved data on the server.
                        </li>

                        <li>
                            <strong>Clear Answer:</strong> Clicking this button deletes your saved answer from the server. Once clicked,
                            your old response is removed, and the index background color for that question number returns to
                            <strong>Yellow</strong>
                        </li>

                        <li>
                            <strong>Updating an Answer:</strong> If you realize your answer is wrong but you do know the correct one,
                            simply select the new option and click <strong>Save & Next</strong>. The system will overwrite your old
                            choice with the new one.
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>9. Final Submission of the Exam</h2>
                    <p>
                        Once you have completed all the questions, you must officially submit your test to receive your results.
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>The Submit Button:</strong> After finishing your exam, click on the <strong>Submit</strong> button.
                        </li>
                        <li>
                            <strong>The Confirmation Window:</strong> Clicking this will open a pop-up window showing two options:
                            <strong>Submit</strong> and <strong>Back</strong>.
                        </li>
                        <li>
                            <strong>Final Submission:</strong> If you click the <strong>Submit</strong> button within this pop-up, your test
                            is finally submitted to the server for evaluation.
                        </li>
                        <li>
                            <strong>Returning to the Exam:</strong> If you click the <strong>Back</strong> button, the window will close,
                            and you will return to your exam screen to review or change your answers.
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>10. Terminating or Closing the Test</h2>
                    <p>
                        If you need to exit the test before completing it, follow these guidelines:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>The Exit Button:</strong> To close the test, click on the <strong>"X"</strong> button.
                        </li>
                        <li>
                            <strong>Confirmation Required:</strong> Your exam will be terminated only after you provide confirmation.
                        </li>
                        <li>
                            <strong>Data Warning: </strong> Be very careful—if you choose to close the test this way, all your
                            responses will be deleted by the server and will not be saved.
                        </li>
                    </ul>
                </section>

                <section className="policySection">
                    <h2>11. Important Technical Warnings</h2>
                    <p>
                        To ensure your progress is safe, please keep the following rules in mind:
                    </p>
                    <ul className="policyList">
                        <li>
                            <strong>Do Not Reload:</strong> Never reload or refresh the web page during an active test.
                        </li>
                        <li>
                            <strong>Risk of Reset:</strong> Reloading may cause your test to reset completely.
                        </li>
                        <li>
                            <strong>Session Loss: </strong>Refreshing the page may also force you back to the previous
                            "Test Selection" page, causing you to lose your current session.
                        </li>
                    </ul>
                </section>
            </div>
        </>
    );
};

export default Manual;